import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Calendar,
  AlertTriangle,
  Check,
  TrendingUp,
  HardDrive,
  Users,
  Clock,
  Zap,
  Loader2,
  Film,
  Mic,
  Download,
  Plus,
  Minus,
  LifeBuoy,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSiteContext } from "../../../context/SiteContext";
import http from "../../../services/httpServices";

const planFeatures = {
  basic: [
    "1 Room",
    "Up to 50 interactive participants",
    "Unlimited meeting length",
    "1 Breakout Room",
    "1 Whiteboard",
    "Core Media: Slides, Video, Audio",
    "10 GB storage",
    "Branded backgrounds",
    "Admin portal included",
  ],
  business: [
    "Everything in Basic +",
    "5 Rooms (running in parallel)",
    "100 interactive participants per meeting",
    "Webinars (up to 1,000 attendees)",
    "Multi-platform live streaming (RTMP)",
    "Unlimited breakouts + whiteboards",
    "All Elements unlocked (Links, Banners, Polls, Website Overlays, Prompter + Core Media)",
    "Full Branding Suite (Background, CTA button, Exit URL, Waiting Room, Vanity URL)",
    "R-Link AI Suite: Notetaker + Translation",
    "Admin Portal",
    "50 GB storage",
    "Phone dial-in + conferencing",
  ],
};

const AddonConfirmModal = ({ isOpen, onClose, onConfirm, loading, config }) => {
  if (!isOpen || !config) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-white font-semibold text-lg">{config.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-300">Here's a summary of your charges:</p>

          {config.quantityLine && (
            <p className="text-sm text-gray-300">{config.quantityLine}</p>
          )}

          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white text-sm font-medium">Due today (prorated)</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Based on {config.daysRemaining} day{config.daysRemaining !== 1 ? "s" : ""} remaining
                  {!config.quantityLine ? " in your current billing period" : ""}
                </p>
              </div>
              <p className="text-white font-semibold">${config.proratedAmount}</p>
            </div>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white text-sm font-medium">Starting next billing date</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {config.quantityLine
                    ? "Added to your existing subscription"
                    : `Charged every ${config.billingInterval} on ${formatDate(config.nextBillingDate)}`}
                </p>
              </div>
              <p className="text-white font-semibold">${config.recurringAmount}/{config.billingInterval === "year" ? "yr" : "mo"}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2.5 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : config.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddOnsSection = ({ billingData, onRefresh, isBusinessPlan = false }) => {
  const [roomQty, setRoomQty] = useState(1);
  const [addonLoading, setAddonLoading] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, config: null, action: null });
  const [removeModal, setRemoveModal] = useState({ open: false, type: null, subscriptionItemId: null });

  const extraRooms = billingData?.stripeAddOns?.extraRooms;
  const aiSuite = billingData?.stripeAddOns?.aiSuite;
  // Basic plan default rooms = 1, Business default = 5 (spec Part 4)
  const defaultRoomCount = isBusinessPlan ? 5 : 1;
  const formatPeriodEnd = (ts) => {
    if (!ts) return 'the end of your billing period';
    try {
      return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) { return 'the end of your billing period'; }
  };

  const calcProration = (unitPrice, quantity) => {
    const periodStart = billingData?.currentPeriodStart ? new Date(billingData.currentPeriodStart).getTime() / 1000 : 0;
    const periodEnd = billingData?.currentPeriodEnd ? new Date(billingData.currentPeriodEnd).getTime() / 1000 : 0;
    const now = Date.now() / 1000;
    if (!periodStart || !periodEnd || periodEnd <= periodStart) return { proratedAmount: "0.00", daysRemaining: 0 };
    const daysInPeriod = (periodEnd - periodStart) / 86400;
    const daysRemaining = Math.max(0, (periodEnd - now) / 86400);
    const proratedAmount = ((unitPrice / daysInPeriod) * daysRemaining * quantity).toFixed(2);
    return { proratedAmount, daysRemaining: Math.round(daysRemaining) };
  };

  const billingInterval = billingData?.billingCycle === "annual" ? "year" : "month";

  const openConfirmModal = (type) => {
    if (type === "extraRooms") {
      const isUpdate = extraRooms?.active;
      const addQty = roomQty;
      const unitPrice = billingInterval === "year" ? 120 : 10;
      const { proratedAmount, daysRemaining } = calcProration(unitPrice, addQty);
      const totalRecurring = isUpdate
        ? (extraRooms.quantity + addQty) * unitPrice
        : addQty * unitPrice;
      setConfirmModal({
        open: true,
        config: {
          title: "Add R-Link Extra Rooms",
          quantityLine: `Adding ${addQty} Extra Room${addQty !== 1 ? "s" : ""} @ $${unitPrice}/${billingInterval === "year" ? "year" : "month"} each`,
          proratedAmount,
          daysRemaining,
          recurringAmount: totalRecurring.toFixed(2),
          billingInterval,
          nextBillingDate: billingData?.nextRenewalDate,
          confirmLabel: "Confirm & Add",
        },
        action: () => {
          if (isUpdate) {
            handleUpdateAddon(extraRooms.subscriptionItemId, extraRooms.quantity + addQty);
          } else {
            handleAddAddon("extraRooms", addQty);
          }
        },
      });
    } else if (type === "aiSuite") {
      const unitPrice = billingInterval === "year" ? 240 : 20;
      const { proratedAmount, daysRemaining } = calcProration(unitPrice, 1);
      setConfirmModal({
        open: true,
        config: {
          title: "Add AI Suite for Basic",
          quantityLine: null,
          proratedAmount,
          daysRemaining,
          recurringAmount: unitPrice.toFixed(2),
          billingInterval,
          nextBillingDate: billingData?.nextRenewalDate,
          confirmLabel: "Confirm & Pay",
        },
        action: () => handleAddAddon("aiSuite"),
      });
    }
  };

  const handleAddAddon = async (addonType, quantity) => {
    setAddonLoading(addonType);
    setConfirmModal({ open: false, config: null, action: null });
    try {
      const res = await http.post("/auth/billing-addon/add", { addonType, quantity });
      if (res?.success) {
        toast.success("Add-on added successfully");
        await onRefresh();
      } else {
        toast.error(res?.message || "Failed to add add-on");
      }
    } catch (err) {
      toast.error("Failed to add add-on");
    } finally {
      setAddonLoading(null);
    }
  };

  const handleUpdateAddon = async (subscriptionItemId, quantity) => {
    setAddonLoading("extraRooms-update");
    setConfirmModal({ open: false, config: null, action: null });
    try {
      const res = await http.post("/auth/billing-addon/update", { subscriptionItemId, quantity });
      if (res?.success) {
        toast.success("Add-on updated successfully");
        await onRefresh();
      } else {
        toast.error(res?.message || "Failed to update add-on");
      }
    } catch (err) {
      toast.error("Failed to update add-on");
    } finally {
      setAddonLoading(null);
    }
  };

  // Open the confirmation modal; actual removal happens on confirm
  const handleRemoveAddon = (subscriptionItemId, addonType) => {
    setRemoveModal({ open: true, type: addonType, subscriptionItemId });
  };

  // Executed when the user clicks "Remove" in the confirmation modal
  const confirmRemoveAddon = async () => {
    const { type: addonType, subscriptionItemId } = removeModal;
    if (!subscriptionItemId) return;
    setAddonLoading(`${addonType}-remove`);
    try {
      const res = await http.post("/auth/billing-addon/remove", { subscriptionItemId });
      if (res?.success) {
        toast.success("Add-on removed successfully");
        await onRefresh();
      } else {
        toast.error(res?.message || "Failed to remove add-on");
      }
    } catch (err) {
      toast.error("Failed to remove add-on");
    } finally {
      setAddonLoading(null);
      setRemoveModal({ open: false, type: null, subscriptionItemId: null });
    }
  };

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
      <h3 className="text-lg font-semibold text-white mb-6">Add-ons</h3>
      <div className="space-y-6">
        {/* Extra Rooms */}
        <div className="p-5 bg-white/5 rounded-xl border border-white/10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <p className="text-white font-medium">R-Link Extra Rooms</p>
                <span className="text-sm text-gray-400">$10/month each</span>
                {extraRooms?.active && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                    <Check className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              {extraRooms?.active ? (
                <p className="text-sm text-gray-400 mt-1">
                  Currently using {extraRooms.quantity} extra room{extraRooms.quantity !== 1 ? "s" : ""} — ${extraRooms.quantity * 10}/month
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-1">
                  Expand your hosting capacity with additional rooms.
                </p>
              )}
              <div className="flex items-center gap-3 mt-3">
                <span className="text-sm text-gray-400">
                  {extraRooms?.active
                    ? `Add more rooms to your existing ${extraRooms.quantity} extra room${extraRooms.quantity !== 1 ? "s" : ""}:`
                    : "Quantity:"}
                </span>
                <button
                  onClick={() => setRoomQty(Math.max(1, roomQty - 1))}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-white font-medium w-6 text-center">{roomQty}</span>
                <button
                  onClick={() => setRoomQty(roomQty + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              {extraRooms?.active ? (
                <>
                  <button
                    disabled={!!addonLoading}
                    onClick={() => openConfirmModal("extraRooms")}
                    className="px-4 py-2 bg-[var(--bk-primary,#6a1fbf)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addonLoading === "extraRooms-update" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : "Add Rooms"}
                  </button>
                  <button
                    disabled={!!addonLoading}
                    onClick={() => handleRemoveAddon(extraRooms.subscriptionItemId, "extraRooms")}
                    className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                  >
                    {addonLoading === "extraRooms-remove" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : "Remove"}
                  </button>
                </>
              ) : (
                <button
                  disabled={!!addonLoading}
                  onClick={() => openConfirmModal("extraRooms")}
                  className="px-4 py-2 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {addonLoading === "extraRooms" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : "Add Now"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* AI Suite — Basic plan only (Business already includes it per spec) */}
        {!isBusinessPlan && (
        <div className="p-5 bg-white/5 rounded-xl border border-white/10">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <p className="text-white font-medium">AI Suite for Basic</p>
                <span className="text-sm text-gray-400">$20/month</span>
                {aiSuite?.active && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                    <Check className="w-3 h-3" /> Active
                  </span>
                )}
              </div>
              {aiSuite?.active ? (
                <p className="text-sm text-gray-400 mt-1">
                  AI features enabled on your account.
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-1">
                  Unlock AI-powered features for your account.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {aiSuite?.active ? (
                <button
                  disabled={!!addonLoading}
                  onClick={() => handleRemoveAddon(aiSuite.subscriptionItemId, "aiSuite")}
                  className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                >
                  {addonLoading === "aiSuite-remove" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : "Remove"}
                </button>
              ) : (
                <button
                  disabled={!!addonLoading}
                  onClick={() => openConfirmModal("aiSuite")}
                  className="px-4 py-2 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {addonLoading === "aiSuite" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : "Add Now"}
                </button>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      <AddonConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, config: null, action: null })}
        onConfirm={() => confirmModal.action?.()}
        loading={!!addonLoading}
        config={confirmModal.config}
      />

      {/* Remove confirmation modal — spec Part 6 */}
      {removeModal.open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md bg-[#0b1020] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10">
              <h3 className="text-white text-lg font-semibold">
                {removeModal.type === 'aiSuite' ? 'Remove AI Suite for Basic' : 'Remove Extra Rooms'}
              </h3>
            </div>
            <div className="px-6 py-5 text-sm text-gray-300 space-y-3">
              {removeModal.type === 'aiSuite' ? (
                <>
                  <p>Are you sure you want to remove AI Suite from your account?</p>
                  <p>
                    Your AI features will be disabled at the end of your current billing period on{' '}
                    <span className="text-white">{formatPeriodEnd(billingData?.currentPeriodEnd)}</span>.
                  </p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to remove your extra rooms?</p>
                  <p>
                    Your room count will return to <span className="text-white">{defaultRoomCount} room{defaultRoomCount === 1 ? '' : 's'}</span> at the end of your current billing period on{' '}
                    <span className="text-white">{formatPeriodEnd(billingData?.currentPeriodEnd)}</span>.
                  </p>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3">
              <button
                disabled={!!addonLoading}
                onClick={() => setRemoveModal({ open: false, type: null, subscriptionItemId: null })}
                className="px-4 py-2 rounded-lg border border-white/15 text-sm text-white hover:bg-white/5 disabled:opacity-50"
              >
                Keep Add-on
              </button>
              <button
                disabled={!!addonLoading}
                onClick={confirmRemoveAddon}
                className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-sm text-red-300 hover:bg-red-500/30 disabled:opacity-50"
              >
                {addonLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BillingAndUsage = () => {
  const { user } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);

  const currentPlan = billingData?.plan || user?.plan;
  const isBusinessPlan = currentPlan === "Business";
  const isLegacy = billingData?.account_type?.toLowerCase()?.includes("legacy");
  const isComplimentary = billingData?.account_type?.toLowerCase()?.includes("complimentary");

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const res = await http.get("/auth/billing-usage");
      if (res?.success) {
        setBillingData(res.data);
      }
    } catch (err) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const usage = billingData?.usage || {
    activeRooms: 0,
    storageUsedGB: 0,
    attendeesThisMonth: 0,
    hoursStreamed: 0,
  };
  const limits = billingData?.limits || {
    maxRooms: (isBusinessPlan ? 5 : 1) + (user?.addOns?.extraRooms?.enabled ? (user.addOns.extraRooms.quantity || 0) : 0),
    maxStorageGB: isBusinessPlan ? 50 : 10,
    maxAttendees: isBusinessPlan ? 100 : 50,
  };

  const getUsagePercent = (used, max) => Math.min((used / max) * 100, 100);
  const isNearLimit = (used, max) => used / max >= 0.8;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[var(--bk-primary,#6a1fbf)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 p-4 md:p-0">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Billing & Usage</h2>
          <p className="text-gray-400">
            Manage your subscription and monitor usage
          </p>
        </div>
        <a
          href="https://go.r-link.com/account/help"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity whitespace-nowrap"
        >
          🛟 Customer Portal Help Guide
        </a>
      </div>

      {/* Current Plan */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-bold text-white">
                {isBusinessPlan ? "Business" : "Basic"} Plan
              </h3>
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.status === "Active"
                    ? "bg-green-500/20 text-green-400"
                    : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {user?.status || "Active"}
              </span>
            </div>
            {isComplimentary && (
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                <span>Account Type:</span>
                <span className="text-white font-medium">Complimentary</span>
              </div>
            )}
            {!isComplimentary && (
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {isLegacy
                    ? "Legacy Subscriber"
                    : billingData?.billingCycle === "annual"
                      ? "Annual"
                      : "Monthly"}
                </span>
                {billingData?.subscriptionStartDate && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Started {formatDate(billingData.subscriptionStartDate)}
                  </span>
                )}
                {billingData?.nextRenewalDate && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Next invoice {billingData.upcomingInvoiceAmount ? `$${billingData.upcomingInvoiceAmount} on ` : ''}{formatDate(billingData.nextRenewalDate)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            {!isBusinessPlan && !isLegacy && !isComplimentary && (
              <a
                href="https://go.r-link.com/account"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[var(--bk-primary,#6a1fbf)] to-[var(--bk-accent,#00c853)] rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
              >
                <Zap className="w-4 h-4 mr-2" />
                Upgrade to Business
              </a>
            )}
            {!isLegacy && !isComplimentary && (
              <a
                href="https://go.r-link.com/account"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-medium text-white hover:bg-white/10 transition-colors"
              >
                Manage Subscription
              </a>
            )}
          </div>
        </div>
        {isComplimentary && (
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
            <p className="text-gray-300 text-sm">
              Your access has been granted complimentary by R-Link. For billing
              inquiries please contact{" "}
              <a
                href="mailto:support@r-link.com"
                className="text-[var(--bk-primary,#6a1fbf)] hover:underline"
              >
                support@r-link.com
              </a>
            </p>
          </div>
        )}
      </div>

      {/* Payment Method */}
      {!isLegacy && !isComplimentary && <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[var(--bk-primary,#6a1fbf)]" />
            Payment Method
          </h3>
          <a
            href="https://go.r-link.com/account"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs font-medium text-white hover:bg-white/10 transition-colors"
          >
            Update
          </a>
        </div>

        {billingData?.paymentMethod ? (
          <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
            <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white text-xs font-bold">
              {billingData.paymentMethod.brand?.toUpperCase() || "CARD"}
            </div>
            <div>
              <p className="text-white font-medium">
                &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull;
                &bull;&bull;&bull;&bull; {billingData.paymentMethod.lastFour}
              </p>
              <p className="text-sm text-gray-500">
                Expires {billingData.paymentMethod.expiry}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="text-yellow-400 font-medium">
                No payment method on file
              </p>
              <p className="text-sm text-gray-400">
                Add a payment method to continue using R-Link
              </p>
            </div>
          </div>
        )}
      </div>}

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {
            label: "Active Rooms",
            icon: TrendingUp,
            used: usage.activeRooms,
            max: limits.maxRooms,
            unit: "rooms",
          },
          {
            label: "Storage Used",
            icon: HardDrive,
            used: usage.storageUsedGB,
            max: limits.maxStorageGB,
            unit: "GB",
          },
          {
            label: "Total Recordings",
            icon: Film,
            used: usage.totalRecordings || 0,
            max: null,
            unit: "",
          },
          {
            label: "Recording Minutes (This Month)",
            icon: Mic,
            used: usage.recordingMinutesThisMonth || 0,
            max: null,
            unit: "min",
          },
          {
            label: "Attendees This Month",
            icon: Users,
            used: usage.attendeesThisMonth,
            max: null,
            unit: "",
          },
          {
            label: "Hours Streamed (This Month)",
            icon: Clock,
            used: usage.hoursStreamed,
            max: null,
            unit: "hrs",
          },
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-white/5 rounded-2xl border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <stat.icon className="w-5 h-5 text-[var(--bk-primary,#6a1fbf)]" />
                <span className="text-sm font-medium text-gray-300">
                  {stat.label}
                </span>
              </div>
              {stat.max && isNearLimit(stat.used, stat.max) && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
                  Near Limit
                </span>
              )}
            </div>
            <div className="flex items-end justify-between">
              <span className="text-3xl font-bold text-white">
                {typeof stat.used === "number"
                  ? stat.used.toLocaleString()
                  : "0"}
                {stat.unit && (
                  <span className="text-lg text-gray-500 ml-1">
                    {stat.unit}
                  </span>
                )}
              </span>
              {stat.max && (
                <span className="text-sm text-gray-500">
                  / {stat.max} {stat.unit}
                </span>
              )}
            </div>
            {stat.max && (
              <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    isNearLimit(stat.used, stat.max)
                      ? "bg-yellow-500"
                      : "bg-[var(--bk-primary,#6a1fbf)]"
                  }`}
                  style={{
                    width: `${getUsagePercent(stat.used, stat.max)}%`,
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add-Ons (Active Subscribers Only, restricted emails) */}
      {!isLegacy && !isComplimentary && billingData?.subscriptionId && ["hello@r-link.com", "krishan.kothari@r-link.com"].includes(user?.email?.toLowerCase()) && (
        <AddOnsSection
          billingData={billingData}
          onRefresh={fetchBillingData}
          isBusinessPlan={isBusinessPlan}
        />
      )}

      {/* Plan Features Comparison */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white mb-6">
          Plan Features
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              Basic Plan
              {!isBusinessPlan && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--bk-primary,#6a1fbf)]/20 text-[var(--bk-primary,#6a1fbf)]">
                  Current
                </span>
              )}
            </h4>
            <ul className="space-y-3">
              {planFeatures.basic.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-300"
                >
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              Business Plan
              {isBusinessPlan && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--bk-accent,#00c853)]/20 text-[var(--bk-accent,#00c853)]">
                  Current
                </span>
              )}
            </h4>
            <ul className="space-y-3">
              {planFeatures.business.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-gray-300"
                >
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Billing History</h3>
        </div>
        {billingData?.invoices?.length > 0 ? (
          <div className="space-y-3">
            {billingData.invoices.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
              >
                <div>
                  <p className="text-white font-medium">{item.invoice}</p>
                  <p className="text-sm text-gray-500">{item.date}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-white font-medium">{item.amount}</span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.status === "Paid"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {item.status}
                  </span>
                  {item.pdfUrl && (
                    <a
                      href={item.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      title="Download Invoice"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">{isLegacy || isComplimentary ? "No invoices found." : "No billing history available."}</p>
        )}
      </div>
    </div>
  );
};

export default BillingAndUsage;
